import React, { Component } from 'react';
import './marcacoes.css';
import Sdk from 'api.digitalpages.module.sdk.api';
import AuthorService from '../../services/AuthorService';
import { BookmarkManager } from '../../components';

import {
  HeaderShelf,
  SidebarRoundedIcons,
  Footer,
  Preloader
} from '../../components';

const CACHE_KEY = 'view_marcacoes_cache';

export default class ViewMarcacoes extends Component {
  constructor(props) {
    super(props);

    const cached = this.loadFromCache();
    this.state = {
      loading: !cached,
      appointments: cached?.appointments || [],
      currentBookUid: null,
      currentReferenceUid: null,
      authors: []
    };
    
    this.handleBookmarkEvent = this.handleBookmarkEvent.bind(this);
  }

  componentDidMount() {
    this.loadAuthorsAndAppointments();
    // Escutar eventos de marcações do player
    window.addEventListener('message', this.handlePlayerMessage);
  }

  loadFromCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  saveToCache(data) {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch {
      // localStorage cheio ou indisponível
    }
  }

  isEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  loadAuthorsAndAppointments = async () => {
    try {
      const authors = await AuthorService.loadAuthors();
      this.setState({ authors });
      await this.loadAppointments();
    } catch (error) {
      console.error('Error loading authors:', error);
      await this.loadAppointments();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handlePlayerMessage);
  }

  handlePlayerMessage = (event) => {
    console.log('[SUPER-APP] Received player message:', {
      type: event.data?.type,
      origin: event.origin,
      data: event.data,
      timestamp: new Date().toISOString()
    });
    
    if (!event.data || typeof event.data !== 'object') {
      console.log('[SUPER-APP] Invalid message data, ignoring');
      return;
    }

    const { userInteraction, trackProgress, type } = event.data;
    
    if (type === 'RDPEvent:track_progress' && trackProgress) {
      console.log('[SUPER-APP] Processing track progress event:', trackProgress);
      this.handleProgressEvent(trackProgress);
      return;
    }
    
    if (userInteraction) {
      console.log('[SUPER-APP] Processing user interaction:', userInteraction);
      this.handleBookmarkEvent(userInteraction.type, userInteraction.userData);
    }
  }

  handleProgressEvent = async (progressData) => {
    console.log('[SUPER-APP] Handling progress event:', progressData);
    try {
      // TODO: Implementar salvamento de progresso via Interaction API
      console.log('[SUPER-APP] Progress tracking not yet implemented');
    } catch (error) {
      console.error('[SUPER-APP] Error handling progress:', error);
    }
  }

  handleBookmarkEvent = async (type, userData) => {
    console.log('[SUPER-APP] Processing bookmark event:', { type, userData });
    try {
      switch (type) {
        case 'highligth': // Manter typo para compatibilidade
          console.log('[SUPER-APP] Creating highlight');
          await this.createBookmark(userData);
          break;
        case 'note':
          console.log('[SUPER-APP] Updating note');
          await this.updateBookmark(userData);
          break;
        case 'delete':
          console.log('[SUPER-APP] Deleting bookmark');
          await this.deleteBookmark(userData);
          break;
        case 'trackProgress':
          console.log('[SUPER-APP] Legacy trackProgress event received');
          break;
        default:
          console.log('[SUPER-APP] Unknown bookmark event type:', type);
      }
      // Recarregar marcações após qualquer operação
      this.loadAppointments();
    } catch (error) {
      console.error('[SUPER-APP] Error handling bookmark event:', error);
    }
  }

  createBookmark = async (bookmarkData) => {
    const connectors = Sdk.Api.authorization.activeProject?.connectors;
    const connectorUser = connectors?.find(c => c.type === "UserData_1");
    
    if (!connectorUser) {
      throw new Error('UserData connector not found');
    }

    const userDataParams = {
      ...bookmarkData,
      external_type: "mark-pdf",
      target_type: "ManagedContentReference",
      context_type: "ManagedContent",
      name: null,
    };

    const url = `cms/v1.1/user/connector/uid/${connectorUser.uid}/data`;
    return await Sdk.Api.dynamic.bridge(url, userDataParams, "POST");
  }

  updateBookmark = async (bookmarkData) => {
    const bookmarkUid = bookmarkData.uid || this.findBookmarkUid(bookmarkData);
    if (!bookmarkUid) return;

    const url = `cms/v1.1/user/data/uid/${bookmarkUid}`;
    const updates = {
      description: bookmarkData.description,
      external_data: bookmarkData.external_data,
    };
    return await Sdk.Api.dynamic.bridge(url, updates, "PUT");
  }

  deleteBookmark = async (bookmarkData) => {
    const bookmarkUid = bookmarkData.uid || this.findBookmarkUid(bookmarkData);
    if (!bookmarkUid) return;

    const url = `cms/v1.1/user/data/uid/${bookmarkUid}`;
    return await Sdk.Api.dynamic.bridge(url, null, "DELETE");
  }

  findBookmarkUid = (bookmarkData) => {
    // Lógica para encontrar UID da marcação baseado nos dados
    // Similar ao getNoteWithoutUID do usePlayer.ts
    const keys = ["start_position", "end_position", "chapter_index"];
    const { external_data } = bookmarkData;
    
    if (!external_data) return null;
    
    try {
      const data = JSON.parse(external_data);
      // Implementar busca na lista atual de marcações
      // Por simplicidade, retornar null por enquanto
      return null;
    } catch (error) {
      return null;
    }
  }

  loadAppointments = async () => {
    try {
      // Get user data with appointments
      const userData = await this.getUserDataWithAppointments();
      
      // Group by content and count marks
      const marks = [];
      userData.forEach(userDataItem => {
        let mark = marks.find(p => p.uid === userDataItem.contextUid);
        
        if (!mark) {
          mark = {
            uid: userDataItem.contextUid,
            numberMark: 0
          };
          marks.push(mark);
        }
        
        mark.numberMark++;
      });
      
      // Get content details for each marked content
      const appointments = await Promise.all(
        marks.map(async (mark) => {
          const content = await this.getContentDetail(mark.uid);
          if (!content) return null;
          
          return {
            ...content,
            appointmentsLength: mark.numberMark
          };
        })
      );

      const freshData = appointments.filter(item => item);
      const cached = this.loadFromCache();

      if (!cached || !this.isEqual(cached?.appointments, freshData)) {
        this.saveToCache({ appointments: freshData });
        this.setState({
          appointments: freshData,
          loading: false
        });
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      this.setState({ loading: false });
    }
  }

  getUserDataWithAppointments = async () => {
    const result = [];
    let page = 1;
    
    while (true) {
      const url = `cms/v1.1/user/data?page=${page}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      
      console.log(`Page ${page} - Total items:`, response.result?.length);
      
      const appointmentData = response.result.filter(p => 
        p.target_type === "ManagedContentReference" && 
        p.context_type === "ManagedContent"
      );
      
      console.log(`Page ${page} - Filtered appointments:`, appointmentData.length);
      
      result.push(...appointmentData.map(this.toUserData));
      
      if (!response.next_page_query) break;
      page++;
    }
    
    console.log('Total appointments found:', result.length);
    return result;
  }

  getContentDetail = async (contentUid) => {
    try {
      const url = `cms/v1.1/managed/content/group/uid/${contentUid}`;
      const response = await Sdk.Api.dynamic.bridge(url, null, "GET");
      return this.toContent(response);
    } catch (error) {
      console.error('Error loading content:', contentUid, error);
      return null;
    }
  }

  toUserData = (serverData) => ({
    uid: serverData.uid,
    contextType: serverData.context_type,
    contextUid: serverData.context_uid,
    targetType: serverData.target_type,
    targetUid: serverData.target_uid
  })

  toContent = (serverData) => {
    const author = this.state.authors?.length > 0 
      ? AuthorService.updateContentAuthor(serverData, this.state.authors)
      : serverData.properties?.find(p => p.name === "Autor")?.value || 'Autor não disponível';
    
    return {
      uid: serverData.uid,
      title: serverData.name || 'Título não disponível',
      author: author,
      description: serverData.properties?.find(p => p.name === "Descrição")?.value,
      image: this.getImageUrl(serverData.uid)
    };
  }

  onBookClick = (book) => {
    // Navigate to book detail or player
    window.location.hash = `#/content/uid/${book.uid}/detail`;
  }

  getImageUrl(uid) {
      var url = `${Sdk.Api.domain}/cms/v1.1/managed/content/group/uid/${uid}/default/thumb?image_options=width:200&api_env=${Sdk.Api.enviroment}&project_key=${Sdk.Api.projectKey}&access_token=${Sdk.Api.authorization.credential.accessToken}`;
  
      return url;
    }

  render() {
    const { loading, appointments } = this.state;
    
    if (loading) {
      return (
        <Preloader/>
        // <div className="rdp-react-template-view-marcacoes">
        //   <div className="rdp-react-template-view-marcacoes-loading">
        //     <div className="rdp-react-template-view-marcacoes-loader"></div>
        //   </div>
        // </div>
      );
    }

    return (
      <div className="rdp-react-template-view-marcacoes">
        <div className="rdp-react-template-view-marcacoes-header">
          <HeaderShelf
            onSignOut={async () => {
              const { UsersService } = await import('../../services/user');
              await UsersService.signOut();
              window.location = window.location.origin + window.location.pathname + "#/login";
            }}
            renderSearch={false}
          />
        </div>

        <div className="rdp-react-template-view-marcacoes-sidebar">
          <SidebarRoundedIcons />
        </div>

        <div className="rdp-react-template-view-marcacoes-main">
          <div className="rdp-react-template-view-marcacoes-container">
            <div className="rdp-react-template-view-marcacoes-header-section">
              <h1 className="rdp-react-template-view-marcacoes-title">Marcações</h1>
            </div>

            <div className="rdp-react-template-view-marcacoes-content">
              {appointments.length === 0 ? (
                <div className="rdp-react-template-view-marcacoes-empty">
                  <p>Você ainda não possui <strong>Marcações</strong> realizadas em livros</p>
                </div>
              ) : (
                <div className="rdp-react-template-view-marcacoes-items">
                  {appointments.map((book, index) => (
                    <div 
                      key={book.uid || index} 
                      className="rdp-react-template-view-marcacoes-item"
                      onClick={() => this.onBookClick(book)}
                    >
                      <img 
                        className="rdp-react-template-view-marcacoes-item-cover blur" 
                        src={book.image} 
                        alt={book.title}
                        loading="lazy"
                        onLoad={(e) => e.target.classList.add('loaded')}
                      />
                      <div className="rdp-react-template-view-marcacoes-item-title">
                        {book.title}
                      </div>
                      <div className="rdp-react-template-view-marcacoes-item-title" style={{marginTop: '10px', fontWeight: '1000', fontSize: '10px', height: "28px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {book.author}
                      </div>
                      <div className="rdp-react-template-view-marcacoes-count">
                        {book.appointmentsLength > 1 ? `${book.appointmentsLength} marcações` : `${book.appointmentsLength} marcação`}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <Footer/>
        </div>
      </div>
    );
  }
}